import { z } from "zod";
import { MODULE_IDS, WEAPON_IDS } from "./definitions.js";
import { REGIONS, SECTOR_IDS } from "./contracts.js";
import {
  RELAY_CAPTURE_RULES,
  RELAY_PRESTIGE_ID,
  relayCaptureRulesSchema,
} from "./relay.js";
import {
  EVENT_CONTRIBUTION_RULES,
  eventContributionRulesSchema,
} from "./reward-rules.js";
import { ORDINARY_NPC_ARCHETYPES } from "./npcs.js";
import {
  PUBLIC_EVENTS,
  SCHEDULED_PUBLIC_EVENT_TYPES,
  type PublicEventPresentation,
  type PublicEventTerms,
  type PublicEventType,
  type ScheduledPublicEventType,
  type SalvageLayout,
} from "./events.js";
import { truceEventTermsSchema } from "./truce-event-terms.js";

const coordinate = z.number().finite().min(0).max(9600);
const credits = z.string().regex(/^(0|[1-9]\d{0,14})$/);
const components = z.number().int().min(0).max(1_000_000);
const text = z.string().min(1).max(500);
const equipmentKey = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("weapon"), id: z.enum(WEAPON_IDS) }),
  z.strictObject({ kind: z.literal("module"), id: z.enum(MODULE_IDS) }),
  z.strictObject({
    kind: z.literal("passive"),
    id: z.enum(["targeting", "capacitor", "repair-drone", "thrusters"]),
  }),
]);
const layoutSchema: z.ZodType<SalvageLayout, SalvageLayout> = z.strictObject({
  sites: z
    .array(
      z.strictObject({
        x: coordinate,
        y: coordinate.max(2200),
        workSeconds: z.number().finite().min(1).max(30),
      }),
    )
    .length(3),
  waves: z
    .array(z.array(z.enum(ORDINARY_NPC_ARCHETYPES)).min(1).max(4))
    .length(3),
  mines: z
    .array(
      z.strictObject({
        x: coordinate,
        y: coordinate.max(2200),
        radius: z.number().finite().min(1).max(110),
        damage: z.number().finite().min(1).max(1000),
      }),
    )
    .max(12),
  clearanceRadius: z.number().finite().min(0).max(600),
});
export const publicEventPresentationSchema = z
  .strictObject({
    version: z.enum([
      "public-events-v2",
      "public-events-v3",
      "public-events-v4",
    ]),
    variantId: z.string().min(1).max(100),
    title: text,
    description: text,
    condition: text,
    credits,
    components,
    prestigeReward: z.literal(RELAY_PRESTIGE_ID).exactOptional(),
    contributionRules: eventContributionRulesSchema.exactOptional(),
  })
  .refine(
    (terms) =>
      terms.version === "public-events-v2"
        ? terms.contributionRules === undefined
        : terms.version === "public-events-v4"
          ? terms.contributionRules === undefined &&
            terms.prestigeReward === undefined &&
            terms.variantId === "station-truce-v1" &&
            terms.credits === "100" &&
            terms.components === 3
          : (terms.prestigeReward === undefined) ===
            (terms.contributionRules !== undefined),
    {
      message: "Only v3 PvE presentation must include contribution rules",
    },
  ) satisfies z.ZodType<PublicEventPresentation>;
const legacyPublicEventTermsSchema = z
  .strictObject({
    type: z.enum(SCHEDULED_PUBLIC_EVENT_TYPES),
    title: text,
    description: text,
    sectorId: z.enum(SECTOR_IDS),
    x: coordinate,
    y: coordinate.max(2200),
    duration: z.number().finite().min(1).max(3600),
    credits,
    components,
    blueprintUnlocks: z
      .array(equipmentKey)
      .max(18)
      .refine(
        (keys) =>
          new Set(keys.map((key) => `${key.kind}:${key.id}`)).size ===
          keys.length,
        "Blueprint rewards must be distinct",
      ),
    version: z.enum(["public-events-v2", "public-events-v3"]),
    variantId: z.string().min(1).max(100),
    condition: text,
    salvage: layoutSchema.nullable(),
    relay: relayCaptureRulesSchema.exactOptional(),
    contributionRules: eventContributionRulesSchema.exactOptional(),
  })
  .refine(
    (terms) =>
      terms.version === "public-events-v2"
        ? terms.contributionRules === undefined
        : (terms.type !== "relay") === (terms.contributionRules !== undefined),
    {
      message: "Only v3 PvE terms must include contribution rules",
    },
  )
  .refine((terms) => (terms.type === "relay") === (terms.relay !== undefined), {
    message: "Only relay events have capture rules",
  })
  .refine((terms) => terms.type !== "relay" || terms.sectorId === "frontier", {
    message: "The contested relay belongs to open PvP space",
  })
  .refine((terms) => (terms.type === "salvage") === (terms.salvage !== null), {
    message: "Only salvage events have a salvage layout",
  })
  .refine(
    (terms) => {
      const region = REGIONS.find((item) => item.id === terms.sectorId);
      if (!region) return false;
      return (
        [
          terms,
          ...(terms.salvage?.sites ?? []),
          ...(terms.salvage?.mines ?? []),
        ].every(
          (point) =>
            point.x >= region.x &&
            point.x <= region.x + region.width &&
            point.y >= region.y &&
            point.y <= region.y + region.height,
        ) &&
        (!terms.relay ||
          (terms.x - terms.relay.radius >= region.x &&
            terms.x + terms.relay.radius <= region.x + region.width &&
            terms.y - terms.relay.radius >= region.y &&
            terms.y + terms.relay.radius <= region.y + region.height))
      );
    },
    { message: "Event geometry must remain inside its sector" },
  ) satisfies z.ZodType<PublicEventTerms>;

export const publicEventTermsSchema: z.ZodType<
  PublicEventTerms,
  PublicEventTerms
> = z.union([legacyPublicEventTermsSchema, truceEventTermsSchema]);

interface SalvageScenario {
  variantId: string;
  title: string;
  description: string;
  condition: string;
  duration: number;
  credits: string;
  components: number;
  salvage: SalvageLayout;
}
const SALVAGE_SCENARIOS: readonly SalvageScenario[] = [
  {
    variantId: "minefield",
    title: "Минный дрейф",
    description:
      "Три обломка на восточной кромке поля. Обходи мины и мобильных заградителей; разбор не требует зачистки охраны.",
    condition:
      "Разбери три объекта по 4 с. Работать можно под огнём; отход дальше 110 м прерывает разбор.",
    duration: 180,
    credits: "140",
    components: 6,
    salvage: {
      sites: [
        { x: 5650, y: 1810, workSeconds: 4 },
        { x: 5920, y: 1800, workSeconds: 4 },
        { x: 6120, y: 1580, workSeconds: 4 },
      ],
      waves: [["minelayer"], ["raider"], ["minelayer"]],
      mines: [
        { x: 5800, y: 1530, radius: 55, damage: 30 },
        { x: 6070, y: 1330, radius: 55, damage: 30 },
        { x: 6270, y: 1780, radius: 55, damage: 30 },
      ],
      clearanceRadius: 0,
    },
  },
  {
    variantId: "sniper-pocket",
    title: "Снайперский карман",
    description:
      "Ценные узлы между астероидными укрытиями. Ракетные снайперы и ремонтники перекрывают северный проход.",
    condition:
      "Разбери три узла по 5 с. Уничтожь или отведи охрану дальше 260 м от текущего объекта.",
    duration: 210,
    credits: "160",
    components: 7,
    salvage: {
      sites: [
        { x: 4670, y: 1230, workSeconds: 5 },
        { x: 5030, y: 1190, workSeconds: 5 },
        { x: 5520, y: 1010, workSeconds: 5 },
      ],
      waves: [["sniper"], ["support", "sniper"], ["sniper"]],
      mines: [],
      clearanceRadius: 260,
    },
  },
  {
    variantId: "jammed-yard",
    title: "Заглушённый док",
    description:
      "Южная цепочка ремонтных секций. Подавители выжигают энергию, а таранщики вынуждают менять подход к работе.",
    condition:
      "Разбери секции за 3 / 6 / 3 с. Отведи или уничтожь охрану в радиусе 360 м; береги энергию.",
    duration: 210,
    credits: "170",
    components: 8,
    salvage: {
      sites: [
        { x: 4660, y: 1900, workSeconds: 3 },
        { x: 5060, y: 1780, workSeconds: 6 },
        { x: 5340, y: 1480, workSeconds: 3 },
      ],
      waves: [["disruptor"], ["charger"], ["disruptor", "support"]],
      mines: [
        { x: 4900, y: 2010, radius: 45, damage: 24 },
        { x: 5530, y: 1540, radius: 45, damage: 24 },
      ],
      clearanceRadius: 360,
    },
  },
];
const CONDITIONS: Record<ScheduledPublicEventType, string> = {
  escort:
    "Проведи транспорт через три засады: защищай, ремонтируй или сопровождай на маршруте.",
  relay:
    "Удерживай зону одной фракцией 40 с. Для награды будь у узла не менее 8 с и в момент победы; чужая фракция блокирует захват.",
  raid: "Отбей три волны и сохрани ретранслятор.",
  salvage: "Разбери три объекта среди мин и охраны.",
  boss: "Уничтожь разрушитель, избегая обозначенных залпов.",
};

/** A generation starts at one; the snapshot never shares mutable template arrays. */
export function createPublicEventTerms(
  type: PublicEventType,
  generation: number,
): PublicEventTerms {
  if (type === "truce")
    throw new Error("A truce hunt requires a real offender and station");
  if (!Number.isSafeInteger(generation) || generation < 1)
    throw new Error("Public event generation must be a positive safe integer");
  const base = PUBLIC_EVENTS[type];
  const scenario =
    type === "salvage"
      ? SALVAGE_SCENARIOS[(generation - 1) % SALVAGE_SCENARIOS.length]
      : null;
  const firstSite = scenario?.salvage.sites[0];
  return publicEventTermsSchema.parse({
    ...base,
    ...scenario,
    x: firstSite?.x ?? base.x,
    y: firstSite?.y ?? base.y,
    version: "public-events-v3",
    variantId: scenario?.variantId ?? `${type}-classic`,
    condition: scenario?.condition ?? CONDITIONS[type],
    salvage: scenario?.salvage ?? null,
    ...(type === "relay"
      ? { relay: RELAY_CAPTURE_RULES }
      : { contributionRules: EVENT_CONTRIBUTION_RULES }),
  });
}

export function publicEventPresentation(
  terms: PublicEventTerms,
): PublicEventPresentation {
  return publicEventPresentationSchema.parse({
    version: terms.version,
    variantId: terms.variantId,
    title: terms.title,
    description: terms.description,
    condition: terms.condition,
    credits: terms.credits,
    components: terms.components,
    ...(terms.type === "relay" ? { prestigeReward: RELAY_PRESTIGE_ID } : {}),
    ...(terms.contributionRules
      ? { contributionRules: terms.contributionRules }
      : {}),
  });
}
